import React, { useState, useContext, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useHistory } from "react-router-dom";

import Container from "../hoc/container/Container";
import ColourPicker from "../components/colourPicker/ColourPicker";
import PhotoPicker from "../components/photoPicker/PhotoPicker";
import ProfilePic from "../components/profilePic/ProfilePic";
import AreasPicker from "../components/areasPicker/AreasPicker";
import AreasScore from "../components/areasScore/AreasScore";
import ProgressBar from "../components/progressBar/ProgressBar";

import LifeAreasContext from "../contexts/LifeAreasContext";
import UserContext from "../contexts/UserContext";
import WheelSizeContext from "../contexts/WheelSizeContext";

import { setDataInLocalStorage } from "../utils/storage";
import style from "./Onboarding.module.css";

const Onboarding = (props: object) => {
  const history = useHistory();
  // Dimensions
  const wheelSize = useContext(WheelSizeContext);
  // Life Areas
  const { lifeAreas: areasState, dispatchLifeAreas } = useContext(
    LifeAreasContext
  );
  // User Data
  const { state: userState, dispatch: dispatchUser } = useContext(UserContext);
  // Name (local)
  // TODO: Create name input component for use here and in Profile.
  const [name, setName] = useState("");

  // Section navigation
  const [activeSectionIndex, setActiveSectionIndex] = useState(0);

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setName(value);
    },
    []
  );

  const handleNameBlur = () => {
    dispatchUser({ type: "SET_FIRST_NAME", name: name });
  };

  const continuePressed = useCallback(
    (isBack: boolean) => {
      const newIndex = activeSectionIndex + (isBack ? -1 : 1);
      if (newIndex === 4) {
        setDataInLocalStorage("user", userState);
        setDataInLocalStorage("lifeAreas", areasState);
        // Colour is stored in the ColourPicker
        // Navigate to main page
        history.push("/personal");
      } else {
        setActiveSectionIndex(newIndex);
      }
    },
    [activeSectionIndex, history, userState, areasState]
  );

  function canContinue() {
    if (activeSectionIndex === 0 && name.length > 2) return true;
    if (activeSectionIndex === 1 && userState.pic) return true;
    if (activeSectionIndex === 2 && areasState.keys.length === 3) return true;
    if (activeSectionIndex === 3) {
      // Check each score has been set
      let valid = true;
      areasState.keys.forEach((key) => {
        if (!areasState.scores[key]) valid = false;
      });
      return valid;
    }
  }

  // TODO: Add <Prompt> to warn about navigating away before the end
  // https://reacttraining.com/react-router/core/api/Prompt
  const progressPercent = (100 / 4) * activeSectionIndex;

  return (
    <section className={style.main}>
      <AnimatePresence exitBeforeEnter={true}>
        {/* Name */}
        {activeSectionIndex === 0 && (
          <motion.section
            key="welcome"
            className={style.welcomeSection}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <h2>Welcome</h2>
            <Container>
              <p>It’s great to have you here...</p>
              <input
                type="text"
                placeholder="First name"
                value={name}
                onChange={handleNameChange}
                onBlur={handleNameBlur}
              />
            </Container>
          </motion.section>
        )}

        {/* Colour and Photo */}
        {activeSectionIndex === 1 && (
          <motion.section
            key="appearance"
            className={style.appearanceSection}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <h2>Make It Yours</h2>
            {/* TODO: Move the ProfilePic inside the PhotoPicker (and update in Profile) */}
            <div className={style.photoPicker}>
              <PhotoPicker size={wheelSize} />
            </div>
            <ColourPicker />
          </motion.section>
        )}

        {/* Selecting Areas */}
        {activeSectionIndex === 2 && (
          <motion.section
            key="areas"
            className={style.areasSection}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Container>
              <h2>Pick Your 3 Areas</h2>
            </Container>
            <AreasPicker />
          </motion.section>
        )}

        {/* Scoring Areas */}
        {activeSectionIndex === 3 && (
          <motion.section
            key="scoring"
            className={style.scoringSection}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Container containerStyle={{ height: "100%" }}>
              <h2>Rate Your Fulfilment</h2>

              {/* <p style={{ textAlign: "center" }}>
                Between 1 (Awful) and 10 (Incredible)
              </p> */}
              <AreasScore />
            </Container>
          </motion.section>
        )}
      </AnimatePresence>
      {/* Continue/Save Control */}

      <div className={style.controls}>
        <ProgressBar percent={progressPercent} />
        <button
          disabled={activeSectionIndex === 0}
          onClick={() => continuePressed(true)}
        >
          Back
        </button>
        <button
          disabled={!canContinue()}
          onClick={() => continuePressed(false)}
        >
          {activeSectionIndex === 3 ? "Finish" : "Continue"}
        </button>
      </div>
    </section>
  );
};

export default Onboarding;
